/**
 * 序號批次 (/licenses/batches, boards/LicenseBatches.dc.html) — admin+. One row per mint
 * (GET /api/license-batches, now with per-batch activated / revoked counts) and the mint
 * flow whose codes are shown exactly once.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/Button';
import { MintModal } from '@/components/LicenseDialogs';
import { errMsg } from '@/components/Dialogs';
import { usePoll } from '@/lib/usePoll';
import { api } from '@/lib/api';
import { fmtDay } from '@/lib/format';
import { pct } from '@/lib/labels';
import type { LicenseBatch } from '@/types';

export function LicenseBatches() {
  const list = usePoll<LicenseBatch[]>(() => api.listLicenseBatches(), 30_000);
  const [mintOpen, setMintOpen] = useState(false);
  const rows = [...(list.data ?? [])].sort((a, b) => (a.minted_at ?? 0) - (b.minted_at ?? 0));
  const total = rows.reduce((n, b) => n + b.quantity, 0);
  const used = rows.reduce((n, b) => n + (b.activated ?? 0), 0);
  const rev = rows.reduce((n, b) => n + (b.revoked ?? 0), 0);

  return (
    <div className="v2-page">
      <div className="v2-head">
        <div className="titles">
          <Link to="/licenses" className="v2-back">← 軟體序號</Link>
          <h1 className="v2-title">序號批次</h1>
          <p className="v2-sub">每批對應一次出貨或一個合作方;序號在伺服器端產生,明碼只在鑄造完成時顯示一次</p>
        </div>
        <Button variant="primary" onClick={() => setMintOpen(true)}>鑄造新批次</Button>
      </div>

      <section className="panel flush">
        {Boolean(list.error) && !list.data && <div className="error-line" style={{ padding: 'var(--space-3) var(--space-4)' }}>載入失敗:{errMsg(list.error)}</div>}
        <div className="tscroll">
          <table className="v2-table">
            <thead>
              <tr><th>批次</th><th>合作方</th><th>數量</th><th>已兌換</th><th>撤銷</th><th>兌換率</th><th>出貨日</th><th>鑄造日</th><th>備註</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr className="empty-row"><td colSpan={9}>{list.loading ? '載入中…' : '尚無批次;鑄造第一批軟體序號'}</td></tr>
              ) : (
                rows.map((b) => {
                  const p = pct(b.activated ?? 0, b.quantity);
                  return (
                    <tr key={b.batch_id}>
                      <td><Link className="link mono" to={`/licenses?batch=${encodeURIComponent(b.batch_id)}`}>{b.batch_id}</Link></td>
                      <td>{b.partner || '—'}</td>
                      <td className="num">{b.quantity}</td>
                      <td className="num">{b.activated ?? '—'}</td>
                      <td className="num">{b.revoked ?? '—'}</td>
                      <td>
                        <div className="rate">
                          <span className="track"><span className="fill" style={{ width: `${p}%` }} /></span>
                          <span className="p">{p}%</span>
                        </div>
                      </td>
                      <td className="muted">{b.ship_date || '—'}</td>
                      <td className="muted">{fmtDay(b.minted_at)}</td>
                      <td className="ellipsis muted">{b.notes || ''}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {rows.length > 0 && (
          <div className="pager"><span className="grow">合計 {total} 組 · 已兌換 {used} · 已撤銷 {rev} · 剩餘 {Math.max(0, total - used - rev)}</span></div>
        )}
      </section>

      {mintOpen && <MintModal onClose={() => setMintOpen(false)} onMinted={() => list.refresh()} />}
    </div>
  );
}
